/**
 * Manejo unificado de errores de red + traducción a mensajes humanos.
 *
 * **Por qué este módulo:**
 *
 * Cuando `fetch()` falla a nivel de red (CORS bloqueado, DNS, TLS,
 * server caído antes de responder, browser offline) lanza un
 * `TypeError` cuyo `.message` es **técnico y poco claro**:
 *
 * - Chrome: "Failed to fetch"
 * - Firefox: "NetworkError when attempting to fetch resource."
 * - Safari: "Load failed" o "The Internet connection appears to be offline."
 *
 * Mostrar eso al funcionario municipal no aporta nada — solo lo
 * confunde. Esta capa convierte cualquier fallo de red en un mensaje
 * accionable en español, y los errores HTTP sin `detail` del backend
 * en mensajes razonables por status code.
 *
 * **Uso:**
 *
 * ```ts
 * const res = await safeFetch(url, { headers });
 * // safeFetch throwea ApiError con mensaje humano si la red falla.
 * if (!res.ok) {
 *   throw await apiErrorFromResponse(res);
 * }
 * ```
 *
 * `ApiError` se exporta desde aquí para que sea el punto único de
 * verdad — `lib/api.ts` lo re-exporta por retrocompatibilidad.
 */

import { notifyAccessDenied } from "./access-denied";

/**
 * Status codes sintéticos para errores que no vienen de HTTP:
 * - `0`: error de red (no llegó respuesta del server).
 *
 * Las páginas pueden discriminar `err.status === 0` para mostrar UX
 * distinta (ej. "Reintentar" en vez de "Recargar página").
 */
export const NETWORK_ERROR_STATUS = 0;

/** Mensaje user-facing cuando la red falla (no hay HTTP response). */
export const NETWORK_ERROR_MESSAGE =
  "No pudimos conectar con Kaptar. Revisa tu conexión a internet e inténtalo de nuevo. Si el problema persiste, escríbenos.";

/**
 * Mensaje fallback por status code cuando el backend no devolvió un
 * `detail` legible (ej. crash que se queda en 500 con HTML genérico
 * de Railway). No reemplaza al `detail` del backend cuando existe —
 * solo entra cuando NO hay nada mejor.
 */
export function defaultMessageForStatus(status: number): string {
  if (status === 400)
    return "La solicitud tiene datos inválidos. Revisa los campos e inténtalo de nuevo.";
  if (status === 401) return "Tu sesión expiró. Vuelve a iniciar sesión.";
  if (status === 403) return "No tienes permisos para esta acción.";
  if (status === 404) return "No encontramos lo que buscabas. Es posible que ya no exista.";
  if (status === 409) return "Esta acción no es posible en el estado actual.";
  if (status === 413) return "El archivo supera el tamaño máximo permitido.";
  if (status === 422)
    return (
      "Algo en la información no quedó bien. Revisa los datos del formulario " +
      "e intenta de nuevo. Si el problema persiste, escríbenos."
    );
  if (status === 429) return "Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.";
  if (status >= 500 && status < 600) {
    return "Tuvimos un problema en el servidor. Inténtalo de nuevo en un momento. Si persiste, escríbenos.";
  }
  return "Algo salió mal. Inténtalo de nuevo.";
}

/**
 * Error tipado del cliente HTTP. `status` puede ser:
 * - 1xx-5xx: HTTP status del backend.
 * - `NETWORK_ERROR_STATUS` (0): red no respondió.
 *
 * `message` SIEMPRE en español, listo para mostrar al usuario.
 */
export class ApiError extends Error {
  readonly status: number;
  /**
   * Payload completo de `detail` cuando el backend devuelve un objeto
   * (ej. `{reason: "ai_quota_exhausted", upgrade_to: null}` en 402).
   * Null cuando el detail era un simple string o no había body JSON.
   *
   * Casi todos los endpoints devuelven `detail: string`. Solo unos
   * pocos (gating tier, billing) usan estructura — por eso este
   * campo es opcional y los callers chequean su forma con type guards.
   */
  readonly detailData: Record<string, unknown> | null;
  constructor(status: number, message: string, detailData: Record<string, unknown> | null = null) {
    super(message);
    this.status = status;
    this.detailData = detailData;
    this.name = "ApiError";
  }

  /** Conveniencia para discriminar errores de red en .catch / .onError. */
  isNetworkError(): boolean {
    return this.status === NETWORK_ERROR_STATUS;
  }
}

/**
 * Detección de errores de red lanzados por `fetch()`.
 *
 * Cuando el navegador no puede contactar el server (offline, CORS,
 * DNS, certificado), `fetch()` rechaza la promesa con un `TypeError`.
 * El mensaje varía entre browsers; lo más estable es chequear el tipo.
 *
 * `DOMException` con name `AbortError` también pasa por acá (cancel
 * vía AbortSignal); se maneja como error de red benigno.
 */
export function isFetchNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (err instanceof DOMException && err.name === "AbortError") return true;
  return false;
}

/**
 * Wrapper de `fetch()` que convierte cualquier fallo de red en un
 * `ApiError` con mensaje humano. Si el fetch responde (incluso con
 * 5xx), pasa la `Response` tal cual al caller — esa parte la maneja
 * `apiErrorFromResponse` cuando `!res.ok`.
 *
 * **Sprint 18.6.1 (hotfix, mayo 20, 2026)**: cuando capturamos un error
 * de red, logueamos a `console.error` con la URL + método + mensaje
 * original del browser. Esto permite que el founder o un dev vea en
 * DevTools si es CORS (`TypeError: Failed to fetch` con `cors` mode),
 * server caído, DNS, etc. — sin esto, el mensaje user-facing
 * "No pudimos conectar..." era opaco para diagnóstico.
 */
export async function safeFetch(
  input: Parameters<typeof fetch>[0],
  init?: Parameters<typeof fetch>[1],
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    if (isFetchNetworkError(err)) {
      // Diagnostic logging para developers — invisible al usuario final
      // pero ayuda a discriminar CORS vs server caído vs DNS al abrir
      // DevTools. El mensaje original del browser distingue:
      // - Chrome CORS: "Failed to fetch" + Network tab muestra preflight failed
      // - Chrome offline: "Failed to fetch" + Network tab muestra (failed)
      // - Firefox CORS: "NetworkError when attempting to fetch resource."
      const urlForLog = typeof input === "string" ? input : input.toString();
      const methodForLog = init?.method ?? "GET";
      const originalMessage = err instanceof Error ? err.message : String(err);
      console.error(
        `[safeFetch] Network error on ${methodForLog} ${urlForLog}: ${originalMessage}. ` +
          `Posibles causas: (1) CORS preflight rechazado por el backend, ` +
          `(2) backend caído o reiniciando, (3) navegador sin conexión, ` +
          `(4) DNS o certificado TLS roto.`,
      );
      throw new ApiError(NETWORK_ERROR_STATUS, NETWORK_ERROR_MESSAGE);
    }
    // Caso muy raro — fetch lanzó algo que no es TypeError. Lo
    // re-throweamos sin envolver para no esconder bugs reales.
    throw err;
  }
}

/**
 * Frases HTTP por defecto, en inglés, que FastAPI pone como `detail` cuando
 * nadie escribió un mensaje: una ruta inexistente responde
 * `{"detail": "Not Found"}` y un `HTTPException` sin detail, la frase de su
 * status. No dicen qué pasó ni qué hacer, así que se tratan como si no
 * hubiera `detail` (issue #561). Un 5xx con mensaje redactado por el backend
 * (p. ej. los 503 curados) no está acá y se muestra tal cual.
 */
const FRASES_HTTP_POR_DEFECTO = new Set([
  "bad request",
  "unauthorized",
  "forbidden",
  "not found",
  "method not allowed",
  "conflict",
  "payload too large",
  "request entity too large",
  "unprocessable entity",
  "unprocessable content",
  "too many requests",
  "internal server error",
  "bad gateway",
  "service unavailable",
  "gateway timeout",
]);

/**
 * Construye un `ApiError` a partir de una `Response` que el backend
 * marcó como no-OK (status >= 400 típicamente). Intenta leer
 * `body.detail` (FastAPI lo pone para errores estructurados); si el
 * body no es JSON o no tiene `detail`, cae al mensaje por status.
 */
export async function apiErrorFromResponse(res: Response): Promise<ApiError> {
  // 403 = el servidor niega el acceso. `MunicipalityProvider` escucha esto para
  // revisar si la membresia se retiro y sacar al usuario (ver `access-denied.ts`).
  if (res.status === 403) notifyAccessDenied(res.url ?? "");
  let detail: string | null = null;
  let detailData: Record<string, unknown> | null = null;
  try {
    const body = (await res.json()) as { detail?: unknown };
    if (
      typeof body.detail === "string" &&
      body.detail.trim().length > 0 &&
      !FRASES_HTTP_POR_DEFECTO.has(body.detail.trim().toLowerCase())
    ) {
      // El backend envía un string user-facing (ej. "No se puede radicar
      // el expediente con items obligatorios pendientes: X, Y"). Lo
      // pasamos al UI tal cual — el backend ya curó el copy.
      detail = body.detail.trim();
    } else if (Array.isArray(body.detail)) {
      // FastAPI 422 (validación Pydantic) — array de errores estructurados
      // `[{loc, msg, type}]`. **NO** mostrar al usuario (es jerga técnica
      // tipo "ensure this value has at least 1 character"). Lo guardamos
      // en detailData.validation_errors para callers que quieran hacer
      // error-por-campo (futuro), y lo logueamos a console.error para que
      // sea visible al developer abriendo DevTools. El mensaje user-facing
      // queda en el fallback amable de defaultMessageForStatus(422).
      detailData = { validation_errors: body.detail };
      console.error(`[ApiError ${res.status}] Validación Pydantic falló:`, body.detail);
    } else if (body.detail !== null && typeof body.detail === "object") {
      // Detail estructurado (ej. {reason, upgrade_to} en 402).
      // Lo exponemos en `detailData` para que el caller pueda
      // discriminar por código machine-readable.
      detailData = body.detail as Record<string, unknown>;
    }
  } catch {
    // Body no-JSON (ej. crash con HTML genérico) — usa default.
  }
  return new ApiError(res.status, detail ?? defaultMessageForStatus(res.status), detailData);
}
